<script lang="ts" setup>
import { computed, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";

import { FileData, useBroadcast } from "~/stores/broadcast.store";
import { useContacts } from "~/stores/contacts.store";
import FieldTemplateVariables from "~/views/trigger/components/ui/forms/fields/FieldTemplateVariables/FieldTemplateVariables.vue";
import { TemplateVariablesMap } from "~/views/trigger/components/ui/forms/fields/FieldTemplateVariables/types";

const { t } = useI18n();
const broadcastStore = useBroadcast();
const contactsStore = useContacts();

const hasVariables = computed<boolean>(() => {
  return !!Object.keys(broadcastStore.bodyVars).length;
});

const fields = computed<{ text: string; value: string }[]>(() => {
  const contacts = contactsStore.selectedContacts;

  const systemFields = contactsStore.systemFields
    .filter((field) => {
      return ["phone", "tg_nick", "birthday", "name"].includes(field.name);
    })
    .map((field) => ({
      ...field,
      name: t(`customers.header.${field.name}`),
    }));

  const customFields = contactsStore.customFields;

  return [...systemFields, ...customFields].map((field) => ({
    text: field.name,
    value: field.id,
    warn: contacts.some((c) => {
      return (
        !c.customFieldValues.find((f) => f.customField.id === field.id) &&
        !c.sysFieldValues.find((f) => f.sysField.id === field.id)
      );
    }),
  }));
});

function onValueChanged(event: { data: TemplateVariablesMap }) {
  broadcastStore.syncVariables({
    bodyVars: event.data.bodyVars,
    headerVars: event.data.headerVars,
    buttonVars: event.data.buttonVars,
  });
}

function onFileChanged(event: { data: FileData | null }) {
  broadcastStore.setHeaderFileV2(event.data);
}

function onErrorsChanged(event: any) {
  if (event.hasErrors) {
    broadcastStore.variablesHasErrors = !!event.hasErrors;
  } else {
    broadcastStore.variablesHasErrors = false;
  }
}
onBeforeUnmount(() => {
  broadcastStore.variablesHasErrors = false;
});
</script>

<template>
  <div v-if="broadcastStore.selectedTemplate" class="variables-form">
    <p v-if="hasVariables" class="title">
      {{ $t("broadcast.content") }}
    </p>

    <FieldTemplateVariables
      v-if="broadcastStore.selectedTemplate !== null"
      :template="broadcastStore.selectedTemplate"
      :template-variables="broadcastStore.variablesMap"
      :variable-generic-options="fields"
      :headerFile="null"
      :key="broadcastStore.selectedTemplate?.id"
      @value-changed="onValueChanged"
      @file-changed="onFileChanged"
      @errors-changed="onErrorsChanged"
    />
  </div>
</template>

<style lang="scss" scoped>
.variables-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
